import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { filter, first, reduce } from "lodash";
import { Button } from "@/shared/ui/Button";
import { Touchable } from "@/shared/ui/Touchable";
import { Row } from "@/shared/ui/Blocks";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ModalFileName } from "@/shared/modals";
import { DimensionContext } from "@/types/data";
import { InsightCard } from "@/shared/ui/InsightCard";
import { CompetencyInsight } from "@/shared/hooks/insights/useCompetencyInsights";
import { ValuesInsight } from "@/shared/hooks/insights/useValuesInsights";
import { CandidateCompetencyInsight } from "@/shared/hooks/insights/useCandidateCompetencyInsights";
import { BaseInsight } from "@/shared/hooks/insights/Insight.types";

interface Filter {
  key: string;
  label: string;
  hidden?: boolean;
}

type Filters = Filter[];

interface InsightsSectionProps {
  insights: {
    [key: string]:
      | CompetencyInsight[]
      | ValuesInsight[]
      | CandidateCompetencyInsight[];
  };
  filters: Filters;
  context?: DimensionContext;
  showFullInsights?: boolean;
}

function InsightsSection({
  insights,
  filters,
  context,
  showFullInsights,
}: InsightsSectionProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState("all");
  const [showMore, setShowMore] = useState(showFullInsights);

  const [openDimensionDetailsModal] = useModal(ModalFileName.DIMENSION_DETAILS);

  const insightCards = useMemo(() => {
    const allInsights = reduce(
      insights,
      (acc, value) => [...acc, ...value],
      [] as BaseInsight[]
    );

    const filteredInsights =
      selected === "all"
        ? allInsights
        : filter(allInsights, (insight) => insight.type === selected);

    return showMore ? filteredInsights : filteredInsights.slice(0, 6);
  }, [insights, selected, showMore]);

  return (
    <div className="w-full flex flex-col justify-center items-center mt-8">
      {!showFullInsights && (
        <Row className="w-full gap-4 items-start mb-8 print:hidden">
          {filters.map((filter) => (
            <Button
              key={filter.key}
              onClick={() => setSelected(filter.key)}
              variant={selected === filter.key ? "secondary" : "passive"}
              size="md"
            >
              {filter.label}
            </Button>
          ))}
        </Row>
      )}
      <div className="gap-4 columns-3">
        {insightCards.map((insight, index) => {
          return (
            <Touchable
              key={`insight-${insight.dimension.id}-${index}`}
              className="mb-8"
              onClick={() =>
                openDimensionDetailsModal({
                  dimension: insight.dimension,
                  allDimensions: insightCards.map((i) => i.dimension),
                  allInsights: insightCards,
                  context,
                  tags: insight.tags,
                  insight,
                })
              }
            >
              <InsightCard
                insight={
                  insight as
                    | CompetencyInsight
                    | ValuesInsight
                    | CandidateCompetencyInsight
                }
                person={context === "team" ? undefined : first(insight.people)}
              />
            </Touchable>
          );
        })}
      </div>
      {insightCards.length === 0 && (
        <div className="w-full text-center">
          <p className="text-gray-500">
            {t("competency_insights.no_insights_available")}
          </p>
        </div>
      )}
      {!showFullInsights && insightCards.length > 6 && (
        <div className="mt-4">
          <Button variant="outline" onClick={() => setShowMore(!showMore)}>
            {showMore ? t("common.show_less") : t("common.show_more")}
          </Button>
        </div>
      )}
    </div>
  );
}

export { InsightsSection };
